import { theme } from '@arnold/common';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipContent, TooltipItem } from './StyledComponents';
import { AnswerResult } from './pulserHelpers';

interface IProps {
  answers: AnswerResult[];
  notRelevantTotalCount: number;
  showEscapeOption: boolean;
  translationNamespace?: string;
}

/**
 * Reusable tooltip content for Pulser charts
 * Shows answer distribution with colored indicators
 */
const PulserChartTooltip: FC<IProps> = ({
  answers,
  notRelevantTotalCount,
  showEscapeOption,
  translationNamespace = 'Report',
}) => {
  const { t } = useTranslation(translationNamespace);

  const getHighlightColor = (option: AnswerResult, index: number, array: AnswerResult[]) => {
    const previousLevels = array.slice(0, index).filter((a) => a.level === option.level);
    const colors = theme.colors.chart[option.level as keyof typeof theme.colors.chart];
    return colors[previousLevels.length % colors.length] ?? colors[0];
  };

  return (
    <TooltipContent chartOptions={answers}>
      {answers.map((option, index, array) => {
        const highlightColor = getHighlightColor(option, index, array);
        const percent = option.percentage;
        const count = option.responses;

        return (
          <TooltipItem key={index}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing.c,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  backgroundColor: highlightColor,
                  borderRadius: '50%',
                }}
              />
              <div style={{ fontWeight: 700 }}>{percent.toFixed(0)}%</div>
            </div>
            <div style={{ fontWeight: 700, marginLeft: theme.spacing.e }}>
              {t('PulserReport:numberOfAnswers', { count, postProcess: 'interval' })}
            </div>
            <div style={{ marginLeft: theme.spacing.e }}>{option.answer}</div>
          </TooltipItem>
        );
      })}
      {showEscapeOption && (
        <TooltipItem>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing.c,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                backgroundColor: theme.colors.text.secondary,
                borderRadius: '50%',
              }}
            />
            <div style={{ fontWeight: 700 }}>
              {t('PulserReport:numberOfAnswers', { count: notRelevantTotalCount, postProcess: 'interval' })}
            </div>
          </div>
          <div style={{ marginLeft: theme.spacing.e }}>{t('bubble:scaleEscapeOption')}</div>
        </TooltipItem>
      )}
    </TooltipContent>
  );
};

export default PulserChartTooltip;
